<script setup lang="ts">
import { watch } from 'vue'

import {
  chartColor,
  formatCompact,
  formatDecimal,
  useChartJs
} from '@/shared/lib'

import type { ColumnChartProps } from './types'

const props = withDefaults(defineProps<ColumnChartProps>(), {
  categoryField: 'date',
  valueField: 'value',
  formatValue: (value: number) => formatDecimal(value),
  height: 500
})

const { element, render } = useChartJs({
  hasData: () => props.data.length > 0,
  draw: ({ Chart }, host) => {
    const colour = chartColor('--el-color-primary')

    return new Chart(host, {
      type: 'bar',
      data: {
        labels: props.data.map((row) => String(row[props.categoryField])),
        datasets: [
          {
            label: props.title,
            data: props.data.map((row) => Number(row[props.valueField] ?? 0)),
            backgroundColor: colour,
            borderColor: colour
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: (items) => `${props.title} on ${items[0]?.label ?? ''}`,
              label: (item) => props.formatValue(Number(item.raw))
            }
          }
        },
        scales: {
          // Tilted rather than thinned: a month of dates upright drops every other label.
          x: { ticks: { autoSkip: false, maxRotation: 60, minRotation: 60 } },
          y: {
            beginAtZero: true,
            ticks: { callback: (value) => formatCompact(Number(value)) }
          }
        }
      }
    })
  }
})

watch(() => props.data, render, { flush: 'post' })
</script>

<template>
  <div class="column-chart" :style="{ height: `${height}px` }">
    <canvas ref="element" />
  </div>
</template>

<style lang="scss" scoped>
.column-chart {
  position: relative;
}
</style>
